import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  Typography,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { MuiOtpInput } from "mui-one-time-password-input";
import { CustomInput } from "global/components";
import type { RequestParticipant } from "models/interfaces";
import inkfreeLogo from "assets/images/signer_inkfree_logo.svg";
import {
  sendOtp,
  verifyOtp,
  sendPhoneOtp,
  verifyPhoneOtp,
  verifySecurityCode,
} from "../ParticipantScreenService";
import {
  handleErrorMessage,
  isValidOTP,
  openSuccessNotification,
} from "helpers/methods";
import strings from "global/constants/StringConstants";
import { useAppDispatch } from "redux/hooks";
import { setReduxSelectedParticipant } from "redux/appSlice";
import { inkfree, signer } from "theme/colors";
import SignerCard from "./SignerCard";
import { CARD } from "./SignerStatusCard.tokens";

const MAX_ATTEMPTS = strings.verificationMaxAttempts;

// Response shape from /verify/otp and /verify/security/code.
interface VerifyResponse {
  verified?: boolean;
  code?: number;
  shortMessage?: string;
  message?: string;
  remainingAttempts?: number;
}

const extractCode = (resOrErr: any): number | undefined => {
  // The body shape is the same whether the backend returns HTTP 200 with
  // `code` in the envelope or throws HTTP 429 (makeCall throws the body, which
  // still has `code`). Status/statusCode are added by HTTP error wrappers.
  return resOrErr?.code ?? resOrErr?.status ?? resOrErr?.statusCode;
};

// White stroke glyphs for the brand circular badge (envelope = OTP, padlock =
// security code). Traced to match the signer status-card badges.
const MailGlyph = ({ size = 30 }: { size?: number }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="#fff"
    strokeWidth={2}
    width={size}
    height={size}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <rect x="3" y="5" width="18" height="14" rx="2" />
    <path d="M3.5 7l8.5 6 8.5-6" />
  </svg>
);

const LockGlyph = ({ size = 30 }: { size?: number }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="#fff"
    strokeWidth={2}
    width={size}
    height={size}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <rect x="4" y="10" width="16" height="10" rx="2" />
    <path d="M8 10V7a4 4 0 0 1 8 0v3" />
    <circle cx="12" cy="15" r="1.4" fill="#fff" stroke="none" />
  </svg>
);

const PhoneGlyph = ({ size = 30 }: { size?: number }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="#fff"
    strokeWidth={2}
    width={size}
    height={size}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2.1 4.2 2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1 1 .4 2 .7 2.9a2 2 0 0 1-.4 2.1L8.1 10a16 16 0 0 0 6 6l1.3-1.3a2 2 0 0 1 2.1-.4c.9.3 1.9.6 2.9.7a2 2 0 0 1 1.6 2z" />
  </svg>
);

const NeedVerificationContent = ({
  needOTPValidation,
  needPhoneValidation,
  authPhoneHint,
  reqToken,
  loading,
  setLoading,
  onVerificationSuccess,
  selectedParticipant,
}: {
  needOTPValidation: boolean;
  /** Phone (SMS) OTP — same code-entry UI as email OTP, different channel. */
  needPhoneValidation?: boolean;
  /** Masked delivery phone (e.g. "••••4567") for the "code sent to …" copy. */
  authPhoneHint?: string | null;
  reqToken: string;
  loading: boolean;
  setLoading: (v: boolean) => void;
  onVerificationSuccess: () => void | Promise<void>;
  selectedParticipant: RequestParticipant;
}) => {
  const dispatch = useAppDispatch();
  const initialFailedAttempts =
    (selectedParticipant as any)?.requestSecurity?.failedAttempts ?? 0;

  // Email and phone OTP share the same one-time-code UI but hit different
  // endpoints — treat them as one "OTP channel" everywhere except the API call
  // and the copy/icon, and keep their resend state separate per channel.
  const isOtpChannel = needOTPValidation || !!needPhoneValidation;
  const otpResendStateKey = `otpResendState:${reqToken}:${
    needPhoneValidation ? "phone" : "email"
  }`;
  const persistedResendState = (() => {
    try {
      const raw = localStorage.getItem(otpResendStateKey);
      if (!raw) return {};
      return JSON.parse(raw) as {
        remainingResends?: number | null;
        otpSendDisabled?: boolean;
        otpSent?: boolean;
      };
    } catch {
      return {};
    }
  })();

  const [otp, setOtp] = useState("");
  const [securityCode, setSecurityCode] = useState("");
  const [otpSent, setOtpSent] = useState<boolean>(
    !!persistedResendState.otpSent,
  );
  const [error, setError] = useState("");
  const [sendingOtp, setSendingOtp] = useState(false);
  const [verifyingOtp, setVerifyingOtp] = useState(false);
  const [verifyingSecurityCode, setVerifyingSecurityCode] = useState(false);
  const [resendSecondsLeft, setResendSecondsLeft] = useState(0);
  const [otpValidateOnSubmit, setOtpValidateOnSubmit] = useState(false);
  const [remainingAttempts, setRemainingAttempts] = useState<number>(
    Math.max(0, MAX_ATTEMPTS - initialFailedAttempts),
  );

  const [otpSendDisabled, setOtpSendDisabled] = useState<boolean>(
    !!persistedResendState.otpSendDisabled,
  );

  const [remainingResends, setRemainingResends] = useState<number | null>(
    typeof persistedResendState.remainingResends === "number"
      ? persistedResendState.remainingResends
      : null,
  );

  const isLocked = remainingAttempts <= 0;

  const isResendLimitReached = otpSendDisabled || remainingResends === 0;

  const RESEND_COOLDOWN_SECONDS = 60;

  useEffect(() => {
    if (resendSecondsLeft <= 0) return;
    const timer = setInterval(() => {
      setResendSecondsLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [resendSecondsLeft]);

  useEffect(() => {
    try {
      localStorage.setItem(
        otpResendStateKey,
        JSON.stringify({ remainingResends, otpSendDisabled, otpSent }),
      );
    } catch {
      // localStorage may be unavailable (private mode etc.) — non-fatal.
    }
  }, [otpResendStateKey, remainingResends, otpSendDisabled, otpSent]);

  const markLocked = () => {
    dispatch(
      setReduxSelectedParticipant({
        requestSecurity: {
          ...(selectedParticipant as any)?.requestSecurity,
          failedAttempts: MAX_ATTEMPTS,
        },
      }),
    );
  };

  const applyRemaining = (next: number | undefined) => {
    if (typeof next === "number" && Number.isFinite(next)) {
      setRemainingAttempts(Math.max(0, Math.min(MAX_ATTEMPTS, next)));
    }
  };

  const buildInvalidMessage = (res: VerifyResponse): string => {
    const base = res?.message || "Incorrect code.";
    const left =
      typeof res?.remainingAttempts === "number"
        ? res.remainingAttempts
        : remainingAttempts;
    if (left <= 0) return base;
    if (left === 1) {
      return `${base} 1 attempt remaining before this link is locked.`;
    }
    return `${base} ${left} attempts remaining.`;
  };

  const handleVerifyResponse = async (
    res: VerifyResponse,
    opts: { offerResendOnExpire?: boolean } = {},
  ) => {
    applyRemaining(res?.remainingAttempts);
    const code = extractCode(res);
    switch (code) {
      case 200:
        setError("");
        await onVerificationSuccess();
        return;
      case 408:
        // Already verified for this session — silently advance.
        setError("");
        await onVerificationSuccess();
        return;
      case 401:
        setError(res?.message || "Code expired. Please request a new one.");
        if (opts.offerResendOnExpire) setOtp("");
        return;
      case 400:
        setError(buildInvalidMessage(res));
        return;
      case 429:
        markLocked();
        return;
      default:
        // Legacy fallback: trust the `verified` boolean.
        if (res?.verified) {
          setError("");
          await onVerificationSuccess();
        } else {
          setError(res?.message || "Verification failed.");
        }
    }
  };

  const handleVerifyError = (err: any) => {
    const code = extractCode(err);
    if (code === 429) {
      markLocked();
      return;
    }
    // Other transport-level errors — surface to caller-side handler.
    handleErrorMessage(err);
    setError(err?.message || "Verification failed");
  };

  const handleSendOtp = async () => {
    if (isLocked || otpSendDisabled) return;
    setError("");
    setSendingOtp(true);
    try {
      const res = needPhoneValidation
        ? await sendPhoneOtp(reqToken)
        : await sendOtp(reqToken);
      setOtpSent(true);
      setResendSecondsLeft(RESEND_COOLDOWN_SECONDS);
      if (typeof res?.remainingResends === "number") {
        setRemainingResends(
          Math.max(0, Math.floor(res.remainingResends)),
        );
      }
      if (res?.message) {
        openSuccessNotification(res.message);
      } else {
        openSuccessNotification("OTP sent successfully");
      }
    } catch (err: any) {
      const code = extractCode(err);
      if (code === 429) {
        setOtpSendDisabled(true);
        setError(
          err?.message ||
            "OTP cannot be sent right now. Please contact the requester.",
        );
        return;
      }
      handleErrorMessage(err);
      setError(err?.message || "Failed to send OTP");
    } finally {
      setSendingOtp(false);
    }
  };

  const OTP_LENGTH = 4;

  const handleVerifyOtp = async () => {
    if (isLocked) return;
    if (!isValidOTP(otp, OTP_LENGTH)) {
      setOtpValidateOnSubmit(true);
      setError("Please enter the 4-digit code");
      return;
    }
    setError("");
    setVerifyingOtp(true);
    try {
      const res: VerifyResponse = needPhoneValidation
        ? await verifyPhoneOtp(reqToken, otp.trim(), selectedParticipant)
        : await verifyOtp(reqToken, otp.trim(), selectedParticipant);
      await handleVerifyResponse(res, { offerResendOnExpire: true });
    } catch (err: any) {
      handleVerifyError(err);
    } finally {
      setVerifyingOtp(false);
    }
  };

  const handleVerifySecurityCode = async () => {
    if (isLocked) return;
    if (!securityCode.trim()) {
      setError("Please enter the security code");
      return;
    }
    setError("");
    setVerifyingSecurityCode(true);
    try {
      const res: VerifyResponse = await verifySecurityCode(
        reqToken,
        securityCode.trim(),
        selectedParticipant,
      );
      await handleVerifyResponse(res);
      if (extractCode(res) === 200) openSuccessNotification("Verified");
    } catch (err: any) {
      handleVerifyError(err);
    } finally {
      setVerifyingSecurityCode(false);
    }
  };

  const isExpiredError = useMemo(() => /expired/i.test(error), [error]);

  // ── Per-state card content ────────────────────────────────────────────────
  // Three states share one card shell (accent ribbon → logo → badge → title →
  // subtitle → content): "Send OTP", "Enter the 4-digit code", and the
  // access-code entry. Copy, badge glyph and body come from the state below.
  const showEnterCode = isOtpChannel && otpSent;
  const badgeIcon: "mail" | "lock" | "phone" | null = showEnterCode
    ? null
    : needPhoneValidation
      ? "phone"
      : needOTPValidation
        ? "mail"
        : "lock";
  // The hint arrives as "••••4198"; show it as "******4198".
  const phoneLastDigits = (authPhoneHint || "").replace(/\D/g, "");
  const maskedPhone = phoneLastDigits ? `******${phoneLastDigits}` : "on file";
  const title = showEnterCode
    ? needPhoneValidation
      ? "Enter verification code"
      : "Enter the 4-digit code"
    : needPhoneValidation
      ? "Verify your identity"
      : needOTPValidation
        ? "Verify with email OTP"
        : "Enter your security code to continue";
  const subtitle = showEnterCode
    ? needPhoneValidation
      ? phoneLastDigits
        ? `We sent a 4-digit code to ${maskedPhone}.`
        : "We sent a 4-digit code to your mobile number on file."
      : "We’ve emailed you a one-time code. Enter it to view the documents and continue."
    : needPhoneValidation
      ? `For your security, we need to confirm it’s you. We’ll send a code to your mobile number ${maskedPhone}.`
      : needOTPValidation
        ? "We’ll send a one-time code to your email. Verify to view the documents and continue."
        : "Protected by a security code shared by the requester. Enter it to view the document and continue.";

  const otpEntry = (
    <>
      <FormControl
        sx={{ width: "100%", alignItems: "center", justifyContent: "center" }}
      >
        <MuiOtpInput
          length={OTP_LENGTH}
          value={otp}
          onChange={(value: string) => {
            setOtp(value.replace(/[^0-9]/g, ""));
            setError("");
            setOtpValidateOnSubmit(false);
          }}
          sx={{
            gap: 1.5,
            "& .MuiOutlinedInput-root": {
              width: 56,
              minWidth: 56,
              height: 60,
              borderRadius: "12px",
              backgroundColor: signer.surface.subtle,
              "& fieldset": {
                borderColor: signer.border.main,
                borderWidth: "1.5px",
              },
              "&:hover fieldset": { borderColor: signer.border.strong },
              "&.Mui-focused fieldset": {
                borderColor: inkfree.main,
                borderWidth: "2px",
              },
            },
            "& .MuiOutlinedInput-input": {
              py: 0,
              px: 1,
              textAlign: "center",
              fontSize: "1.5rem",
              fontWeight: 700,
              color: signer.ink,
            },
          }}
          TextFieldsProps={{
            variant: "outlined",
            placeholder: "",
            type: "number",
            disabled: isLocked,
            inputProps: {
              inputMode: "numeric",
              pattern: "[0-9]*",
              "aria-label": "OTP digit",
              "aria-describedby":
                (otpValidateOnSubmit && otp.length !== OTP_LENGTH) || !!error
                  ? "otp-error"
                  : undefined,
              "aria-invalid":
                (otpValidateOnSubmit && otp.length !== OTP_LENGTH) || !!error,
            },
            sx: {
              "& input[type=number]": {
                MozAppearance: "textfield",
              },
              "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button":
                {
                  WebkitAppearance: "none",
                  margin: 0,
                },
            },
          }}
        />
        {(error || (otpValidateOnSubmit && otp.length !== OTP_LENGTH)) && (
          <Typography
            id="otp-error"
            role="alert"
            variant="body2"
            color={remainingAttempts === 1 ? "warning.main" : "error"}
            sx={{ display: "block", mt: 1.5, fontWeight: 500 }}
          >
            {error || "Please enter the 4-digit code"}
          </Typography>
        )}
      </FormControl>
      {/* Verify stays enabled once resends run out — the signer may already
          hold a valid, unexpired code and just can't ask for a fresh one. */}
      <Button
        variant="contained"
        size="large"
        onClick={handleVerifyOtp}
        disabled={verifyingOtp || isLocked}
        sx={{ textTransform: "none", minWidth: 160, mt: 0.5 }}
        startIcon={
          verifyingOtp ? (
            <CircularProgress size={18} color="inherit" />
          ) : undefined
        }
      >
        {verifyingOtp ? "Verifying…" : "Verify"}
      </Button>
      {/* The resend control itself stays visible — disabled, not removed —
          once the limit is hit, so the "no resends left" message below has
          something to explain. */}
      {!isLocked &&
        (resendSecondsLeft > 0 && !isExpiredError && !isResendLimitReached ? (
          <Typography variant="body2" color="text.secondary">
            Resend code in {Math.floor(resendSecondsLeft / 60)}:
            {String(resendSecondsLeft % 60).padStart(2, "0")}
          </Typography>
        ) : (
          <Button
            variant="text"
            size="small"
            onClick={handleSendOtp}
            disabled={sendingOtp || isResendLimitReached}
            sx={{ textTransform: "none" }}
            startIcon={
              sendingOtp ? (
                <CircularProgress size={16} color="inherit" />
              ) : undefined
            }
          >
            {sendingOtp ? "Sending…" : "Resend code"}
          </Button>
        ))}
      {!isLocked &&
        (isResendLimitReached ? (
          <Typography
            variant="caption"
            color="warning.main"
            sx={{ fontWeight: 600, textAlign: "center" }}
          >
            No resends remaining. Please contact the requester.
          </Typography>
        ) : (
          remainingResends !== null && (
            <Typography
              variant="caption"
              color={remainingResends <= 1 ? "warning.main" : "text.secondary"}
              sx={{ fontWeight: remainingResends <= 1 ? 600 : 400 }}
            >
              {remainingResends === 1
                ? "1 resend remaining."
                : `${remainingResends} resends remaining.`}
            </Typography>
          )
        ))}
    </>
  );

  const sendOtpEntry = (
    <>
      {isResendLimitReached ? (
        <Typography
          variant="body2"
          color="warning.main"
          sx={{ textAlign: "center", fontWeight: 600 }}
        >
          No resends remaining for this link. Please contact the requester.
        </Typography>
      ) : (
        <Button
          variant="contained"
          size="large"
          onClick={handleSendOtp}
          disabled={sendingOtp || isLocked || otpSendDisabled}
          sx={{ textTransform: "none", minWidth: 160 }}
          startIcon={
            sendingOtp ? (
              <CircularProgress size={18} color="inherit" />
            ) : undefined
          }
        >
          {sendingOtp
            ? "Sending…"
            : needPhoneValidation
              ? "Send verification code"
              : "Send code"}
        </Button>
      )}
      {error && (
        <Typography variant="body2" color="error" role="alert">
          {error}
        </Typography>
      )}
    </>
  );

  const securityCodeEntry = (
    <>
      <Box sx={{ width: "100%", textAlign: "left" }}>
        <CustomInput
          id="securityCode"
          placeHolder="Enter security code"
          type="text"
          name="securityCode"
          label="Security code"
          value={securityCode}
          onChange={(e: any) => {
            setSecurityCode(e.target.value);
            setError("");
          }}
          disabled={isLocked}
          error={!!error}
          helperText={error}
        />
      </Box>
      <Button
        variant="contained"
        size="large"
        onClick={handleVerifySecurityCode}
        disabled={verifyingSecurityCode || isLocked}
        sx={{ textTransform: "none", minWidth: 160 }}
        startIcon={
          verifyingSecurityCode ? (
            <CircularProgress size={18} color="inherit" />
          ) : undefined
        }
      >
        {verifyingSecurityCode ? "Verifying…" : "Verify"}
      </Button>
    </>
  );

  const content = showEnterCode
    ? otpEntry
    : isOtpChannel
      ? sendOtpEntry
      : securityCodeEntry;

  return (
    <SignerCard maxWidth={CARD.width} sx={{ boxShadow: CARD.shadow }}>
      <Box
        sx={{
          px: { xs: 3.5, sm: `${CARD.bodyPadX}px` },
          pt: `${CARD.bodyPadTop}px`,
          pb: `${CARD.bodyPadTop}px`,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
        }}
      >
        <Box
          component="img"
          src={inkfreeLogo}
          alt="Inkfree"
          sx={{ width: CARD.logoWidth, height: "auto" }}
        />
        {badgeIcon && (
          <Box
            aria-hidden
            sx={{
              mt: `${CARD.circleGap}px`,
              width: CARD.circleSize,
              height: CARD.circleSize,
              borderRadius: "50%",
              backgroundColor: inkfree.main,
              boxShadow: "0 10px 26px rgba(221,56,36,0.3)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {badgeIcon === "mail" ? (
              <MailGlyph />
            ) : badgeIcon === "phone" ? (
              <PhoneGlyph />
            ) : (
              <LockGlyph />
            )}
          </Box>
        )}
        <Typography
          sx={{
            mt: `${CARD.titleGap}px`,
            fontSize: CARD.titleSize,
            fontWeight: 800,
            color: CARD.titleColor,
            letterSpacing: "-0.01em",
            lineHeight: 1.25,
          }}
        >
          {title}
        </Typography>
        <Typography
          sx={{
            mt: `${CARD.bodyGap}px`,
            fontSize: CARD.bodySize,
            lineHeight: CARD.bodyLineHeight,
            color: CARD.bodyColor,
            maxWidth: CARD.bodyMaxWidth,
          }}
        >
          {subtitle}
        </Typography>
        <Box
          sx={{
            mt: 3,
            width: "100%",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 1.5,
          }}
        >
          {content}
        </Box>
      </Box>
    </SignerCard>
  );
};

export default NeedVerificationContent;
